import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Layout from "../../../components/Layout";
import PageTitle from "../../../components/PageTitle";
import BreadCrumb from "../../../components/BreadCrumb";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import { useUserState } from "../../../context/UserContext";
import useWindowSize from "../../../hooks/useWindow";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";

const SharedSyllabusList = () => {
  const { id: subjectId } = useParams();
  const navigate = useNavigate();
  const { userRole } = useUserState();
  const width = useWindowSize();
  const mobile = width < 600;

  const isEmployee = userRole?.role_type === "employee";
  const isStudent = userRole?.role_type === "student";

  const [searchResult, setSearchResult] = useState([]);
  const [syllabusList, setSyllabusList] = useState([]);

  const getSyllabuses = () => {
    // Students should only see published syllabuses
    const params = isStudent ? { status: "PUBLISHED" } : {};
    httprequest.get(`${SYLLABUS_ENDPOINT}/subjects/${subjectId}`, { params })
      .then((res) => {
        const items = res?.data?.data?.items || [];
        setSyllabusList(items);
        setSearchResult(items);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to fetch syllabuses");
      });
  };

  useEffect(() => {
    getSyllabuses();
  }, [subjectId]);

  const handleSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    const result = syllabusList?.filter((data) => valArray?.every(
      (word) => data?.session?.name?.toLowerCase().includes(word.toLowerCase()) ||
                data?.term?.name?.toLowerCase().includes(word.toLowerCase())
    ));
    setSearchResult(result);
  };

  const columns = [
    {
      name: "Session",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Session</p>}
          {row?.session?.name}
        </>
      )
    },
    {
      name: "Term",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Term</p>}
          {row?.term?.name}
        </>
      )
    },
    {
      name: "Classes",
      selector: (row) => {
        const classNames = row?.classes?.map((c) => c?.class_name) || [];

        return (
          <>
            {mobile && <p className="mobile-styling">Classes</p>}
            {classNames.join(", ") || "-"}
          </>
        );
      }
    },
    {
      name: "Date Created",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Date Created</p>}
          {row?.created_at?.substring(0, 10)}
        </>
      )
    },
    {
      name: "Date Updated",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Date Updated</p>}
          {row?.updated_at?.substring(0, 10)}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <button
          className="table-action-icon"
          onClick={() => {
            if (isEmployee) {
              navigate(`/class-activities/employee/syllabus/${subjectId}/${row?.id}`);
            } else if (isStudent) {
              navigate(`/class-activities/syllabus/${subjectId}/${row?.id}`);
            }
          }}
        >
          <Icon icon="mdi:chevron-right" />
        </button>
      )
    }
  ];

  const breadcrumbLinkOne = isEmployee ? "/mysubjects" : "/class-activities/syllabus";
  const breadcrumbLinkName = isEmployee ? "My Subjects" : "Subjects";

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one={breadcrumbLinkOne}
            link_one_name={breadcrumbLinkName}
            active="Syllabus Details"
            description="View syllabus content for this subject"
          />

          <PageTitle
            title="Syllabus Details"
          />

          <div className="page-content" style={{ marginTop: "20px" }}>
            <Table
              data={searchResult}
              columns={columns}
              subHeader={true}
              pagination
              subHeaderComponent={
                syllabusList?.length > 0 && (
                  <div className="action_div">
                    <TableSearch
                      placeholder="Search session or term..."
                      searchTableFunc={handleSearch}
                    />
                  </div>
                )
              }
            />
          </div>
        </>
      }
    />
  );
};

export default SharedSyllabusList;
